import { api } from "../../../api/Middleware";
import { METRICS_URL } from "../../../api/Urls";

class DashboardService {
  // Cache para evitar múltiplas requisições simultâneas - aumentado para 24h
  static cache = new Map();
  static cacheTimeout = 24 * 60 * 60 * 1000; // 24 horas (alinhado com backend)

  static getCacheKey(url, params) {
    return `${url}_${JSON.stringify(params || {})}`;
  }

  static setCache(key, data) {
    this.cache.set(key, {
      data,
      timestamp: Date.now(),
    });
  }

  static getCache(key) {
    const cached = this.cache.get(key);
    if (cached && Date.now() - cached.timestamp < this.cacheTimeout) {
      return cached.data;
    }
    this.cache.delete(key);
    return null;
  }

  /**
   * Obtém todas as métricas do dashboard v2 para nichos Saúde e Imobiliário
   * Nova estrutura de resposta:
   * - primary_metrics: manual_appointments, ai_appointments, conversion_rate, contacts_monthly
   * - charts: appointments_by_status_monthly, confirmation_distribution, contacts_monthly_trend, messages_by_date, ai_vs_manual_daily
   * - operational: automations, contacts
   * - metadata: business_segment, data_scope, current_month, cache_duration
   */
  static async getAllMetrics() {
    const cacheKey = this.getCacheKey("dashboard_v3_metrics"); // Atualizado versão do cache
    const cached = this.getCache(cacheKey);
    if (cached) {
      console.log("📊 Dashboard: Dados obtidos do cache local");
      return cached;
    }

    try {
      console.log("📡 Dashboard: Buscando dados do backend...");

      // Requisição para a nova API v3 refatorada
      const response = await api.get(`${METRICS_URL}dashboard/`);
      const data = response.data;

      // Validar estrutura básica da resposta
      if (
        !data.primary_metrics ||
        !data.charts ||
        !data.operational ||
        !data.metadata
      ) {
        console.warn("⚠️ Dashboard: Estrutura de resposta incompleta", data);
        throw new Error("Estrutura de dados inválida recebida do servidor");
      }

      // Validar business_segment
      if (
        !data.metadata.business_segment ||
        !["1", "2"].includes(data.metadata.business_segment)
      ) {
        throw new Error(
          "Dashboard disponível apenas para segmentos Saúde e Imobiliário"
        );
      }

      // Estrutura de dados limpa e organizada
      const result = {
        // MÉTRICAS PRINCIPAIS (5 cards agora)
        primary_metrics: {
          manual_appointments: {
            current_month:
              data.primary_metrics?.manual_appointments?.current_month || 0,
            previous_month:
              data.primary_metrics?.manual_appointments?.previous_month || 0,
            growth_percentage:
              data.primary_metrics?.manual_appointments?.growth_percentage || 0,
            trend:
              data.primary_metrics?.manual_appointments?.trend || "neutral",
          },
          ai_appointments: {
            current_month:
              data.primary_metrics?.ai_appointments?.current_month || 0,
            previous_month:
              data.primary_metrics?.ai_appointments?.previous_month || 0,
            growth_percentage:
              data.primary_metrics?.ai_appointments?.growth_percentage || 0,
            trend: data.primary_metrics?.ai_appointments?.trend || "neutral",
          },
          conversion_rate: {
            conversion_rate:
              data.primary_metrics?.conversion_rate?.conversion_rate || 0,
            total_contacts:
              data.primary_metrics?.conversion_rate?.total_contacts || 0,
            contacts_with_customers:
              data.primary_metrics?.conversion_rate?.contacts_with_customers ||
              0,
            trend: data.primary_metrics?.conversion_rate?.trend || "neutral",
          },
          contacts_monthly: {
            current_month:
              data.primary_metrics?.contacts_monthly?.current_month || 0,
            previous_month:
              data.primary_metrics?.contacts_monthly?.previous_month || 0,
            growth_percentage:
              data.primary_metrics?.contacts_monthly?.growth_percentage || 0,
            trend: data.primary_metrics?.contacts_monthly?.trend || "neutral",
          },
          // ✅ NOVA MÉTRICA: Total de Agendamentos
          total_appointments: {
            total_all_time:
              data.primary_metrics?.total_appointments?.total_all_time || 0,
            total_ai_all_time:
              data.primary_metrics?.total_appointments?.total_ai_all_time || 0,
            total_manual_all_time:
              data.primary_metrics?.total_appointments?.total_manual_all_time ||
              0,
            ai_percentage:
              data.primary_metrics?.total_appointments?.ai_percentage || 0,
            manual_percentage:
              data.primary_metrics?.total_appointments?.manual_percentage || 0,
            trend: data.primary_metrics?.total_appointments?.trend || "neutral",
          },
        },

        // DADOS DOS GRÁFICOS
        charts: {
          // Agendamentos por status nos últimos 6 meses
          appointments_by_status_monthly:
            data.charts?.appointments_by_status_monthly || {},

          // Distribuição de confirmações (donut)
          confirmation_distribution: data.charts?.confirmation_distribution || {
            confirmed: 0,
            pending: 0,
            canceled: 0,
            not_confirmed: 0,
          },

          // Contatos por mês (linha)
          contacts_monthly_trend: data.charts?.contacts_monthly_trend || {},

          // Mensagens por dia (área) - mantido do formato original
          messages_by_date: data.charts?.messages_by_date || [],

          // IA vs Manual diário (linha)
          ai_vs_manual_daily: data.charts?.ai_vs_manual_daily || {
            categories: [],
            ai_series: [],
            manual_series: [],
          },
        },

        // DADOS OPERACIONAIS
        operational: {
          automations: {
            total_automations:
              data.operational?.automations?.total_automations || 0,
            active_automations:
              data.operational?.automations?.active_automations || 0,
          },
          contacts: {
            total_contacts: data.operational?.contacts?.total_contacts || 0,
            contacts_by_column:
              data.operational?.contacts?.contacts_by_column || {},
          },
        },

        // METADADOS
        metadata: {
          business_segment: data.metadata?.business_segment,
          business_segment_name:
            data.metadata?.business_segment === "1"
              ? "Imobiliário"
              : "Saúde e Bem-estar",
          data_scope: data.metadata?.data_scope || "user",
          current_month: data.metadata?.current_month,
          cache_duration: data.metadata?.cache_duration || 86400,
          generated_at: data.metadata?.generated_at || new Date().toISOString(),
        },

        // Para compatibilidade com código legacy (será removido futuramente)
        generatedAt: data.metadata?.generated_at || new Date().toISOString(),
        errors: [],
      };

      // Cache dos dados processados
      this.setCache(cacheKey, result);

      

      return result;
    } catch (error) {
      console.error("❌ Dashboard: Erro ao buscar métricas:", error);

      // Tratamento específico para diferentes tipos de erro
      if (error.response?.status === 403) {
        // Erro de business_segment não permitido
        throw new Error(
          "Dashboard disponível apenas para segmentos Saúde e Imobiliário"
        );
      } else if (error.response?.status === 401) {
        // Erro de autenticação
        throw new Error("Sessão expirada. Faça login novamente.");
      } else if (error.response?.status === 500) {
        // Erro interno do servidor
        throw new Error(
          "Erro interno do servidor. Tente novamente em alguns minutos."
        );
      } else if (!navigator.onLine) {
        // Erro de conexão
        throw new Error("Sem conexão com a internet. Verifique sua conexão.");
      } else {
        // Erro genérico
        throw new Error(error.message || "Erro ao carregar dados do dashboard");
      }
    }
  }

  /**
   * Limpa o cache do dashboard
   * Usado quando o usuário clica em "Atualizar" ou quando há mudanças importantes
   */
  static clearCache() {
    console.log("🧹 Dashboard: Limpando cache local...");
    this.cache.clear();

    // Também limpa o cache do backend via API
    return this.clearServerCache();
  }

  /**
   * Limpa o cache do servidor (backend)
   */
  static async clearServerCache() {
    try {
      console.log("🧹 Dashboard: Limpando cache do servidor...");

      await api.delete(`${METRICS_URL}dashboard/cache/`);

      console.log("✅ Dashboard: Cache do servidor limpo com sucesso");
      return true;
    } catch (error) {
      console.warn(
        "⚠️ Dashboard: Erro ao limpar cache do servidor:",
        error.message
      );
      // Não é um erro crítico, apenas log
      return false;
    }
  }

  /**
   * Obtém informações sobre o cache atual
   */
  static getCacheInfo() {
    const cacheKey = this.getCacheKey("dashboard_v2_metrics");
    const cached = this.cache.get(cacheKey);

    if (cached) {
      const ageMinutes = Math.floor(
        (Date.now() - cached.timestamp) / (1000 * 60)
      );
      const remainingMinutes = Math.max(
        0,
        Math.floor(this.cacheTimeout / (1000 * 60)) - ageMinutes
      );

      return {
        exists: true,
        age_minutes: ageMinutes,
        remaining_minutes: remainingMinutes,
        timestamp: new Date(cached.timestamp).toLocaleString("pt-BR"),
      };
    }

    return {
      exists: false,
      age_minutes: 0,
      remaining_minutes: 0,
      timestamp: null,
    };
  }

  /**
   * Força atualização dos dados (ignora cache)
   */
  static async forceRefresh() {
    console.log("🔄 Dashboard: Forçando atualização...");

    // Limpa cache local e servidor
    await this.clearCache();

    // Busca dados frescos
    return this.getAllMetrics();
  }

  /**
   * Método utilitário para debug - exibe estrutura dos dados
   */
  static async debugDataStructure() {
    try {
      const data = await this.getAllMetrics();

      console.group("📊 Dashboard Debug - Estrutura de Dados");
      console.log("🔧 Metadados:", data.metadata);
      console.log("📈 Métricas Principais:", data.primary_metrics);
      console.log("📊 Gráficos:", Object.keys(data.charts));
      console.log("⚙️ Operacional:", data.operational);
      console.log("💾 Cache Info:", this.getCacheInfo());
      console.groupEnd();

      return data;
    } catch (error) {
      console.error("❌ Debug Error:", error);
      return null;
    }
  }
}

export default DashboardService;
